import {
  memo,
  MouseEventHandler,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { PrimeIcons } from 'primereact/api';
import { Button } from 'primereact/button';
import { confirmPopup, ConfirmPopupProps } from 'primereact/confirmpopup';
import { Menubar } from 'primereact/menubar';
import { MenuItem } from 'primereact/menuitem';
import { UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { AuthenticationType } from '@/app/slices/authentication';
import { addToast } from '@/app/slices/toast';
import { useWorkflowMenu } from '@/components/common/workflow';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsTogActive } from '@/hooks/useIsTogActive';
import { useShipmentPastDueRestriction } from '@/hooks/useShipmentPastDueRestriction';
import {
  useShippingFormTerminalBeef,
  useShippingTripForm,
} from '@/services/reports';
import { useSetInProgress } from '@/services/shipping/hooks';
import { WORKFLOW } from '@/utils';

import { useGenerateShippingForm } from '../../hooks/useGenerateShippingForm';
import { useShouldPromptSemenLab } from '../../hooks/useSemenLabPrompt';
import { useShipmentContext } from '../../hooks/useShipmentContext';
import { useShippingWorkflowMenu } from '../../hooks/useShippingWorkflowMenu';
import type { ShipmentSchema } from '../../utils/schema';
import { ShippingFormExportDialog } from '../shipping-form-export-dialog';

import './index.css';

type ShipmentToolBarProps = {
  onSubmit: () => void;
  setValue: UseFormSetValue<ShipmentSchema>;
  isDirty: boolean;
  disableSave: boolean;
  loading: boolean;
};

export const ShipmentToolBar = memo(
  ({
    onSubmit,
    setValue,
    isDirty,
    disableSave,
    loading,
  }: ShipmentToolBarProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const me = useSelector(
      (state: { authentication: AuthenticationType }) =>
        state.authentication.me,
    );

    const { shipment } = useShipmentContext();
    const shipmentId = shipment.id;
    const confirm = useCallback((options: ConfirmPopupProps) => {
      return new Promise<boolean>((resolve) => {
        confirmPopup({
          ...options,
          accept: () => {
            options.accept?.();
            resolve(true);
          },
          reject: () => {
            options.reject?.();
            resolve(false);
          },
        });
      });
    }, []);
    const handleError = useCallback((error: unknown) => {
      if (error instanceof Error) {
        console.error('An error occurred:', error.message);
      } else {
        console.error('An unknown error occurred:', error);
      }
    }, []);
    const generateShippingForm = useGenerateShippingForm(shipmentId);
    const enableShippingFormExport = useFeatureFlag('shipping_form_export');
    const [exportFormat, setExportFormat] = useState<'pdf' | 'excel' | null>(
      null,
    );
    const shouldPromptSemenLab = useShouldPromptSemenLab();
    const showPedigreeNames = useFeatureFlag(
      'shipping_show_report_params_dialog',
    );
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const shippingMethod = params.get('shipping-method') || 'all-shipments';
    const shipFromSite = params.get('ship-from-site') || 'all';

    const environmentId = me?.environmentId || 0;

    const { mutateAsync: setInProgress } = useSetInProgress();

    const { restrictionEnabled: pastDueRestrictionEnabled, isOwnerRestricted } =
      useShipmentPastDueRestriction();

    const isPastDueRestricted = useMemo(
      () => isOwnerRestricted(shipment?.owner),
      [isOwnerRestricted, shipment?.owner],
    );

    const pastDueRestrictionDetail = useMemo(
      () => ({
        accounts: shipment?.owner?.name ?? shipment?.owner?.code ?? '',
      }),
      [shipment?.owner?.code, shipment?.owner?.name],
    );

    const selectInProgress = useCallback(async () => {
      if (isPastDueRestricted) {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('shipping.pastDueRestriction'),
            detail: t(
              'shipping.pastDueRestrictionInProgress',
              pastDueRestrictionDetail,
            ),
            life: ToastLife.LONG,
          }),
        );
        return;
      }

      try {
        const responseData = await setInProgress(shipmentId, null);
        if (responseData?.shipmentStatus) {
          setValue('shipmentStatus', responseData.shipmentStatus);
        }
      } catch (error) {
        handleError(error);
      }
    }, [
      dispatch,
      handleError,
      isPastDueRestricted,
      pastDueRestrictionDetail,
      setInProgress,
      setValue,
      shipmentId,
      t,
    ]);

    const printShippingForm = useCallback(async () => {
      let shouldSendEmail = false;
      if (
        shouldPromptSemenLab([
          {
            hasSemen: shipment?.hasSemen,
            shipFromSiteId: shipment?.shipFromSiteId,
          },
        ])
      ) {
        shouldSendEmail = await confirm({
          message: t('semen.emailSemenLab'),
          icon: 'pi pi-question-circle',
          defaultFocus: 'accept',
        });
      }

      await generateShippingForm({
        sendEmail: shouldSendEmail,
        shipmentNumbers: shipment?.shipmentNumber
          ? [shipment.shipmentNumber]
          : [],
        showPedigree: showPedigreeNames,
      });
    }, [
      confirm,
      generateShippingForm,
      shipment?.hasSemen,
      shipment?.shipFromSiteId,
      shipment?.shipmentNumber,
      shouldPromptSemenLab,
      showPedigreeNames,
      t,
    ]);

    const handleCloseForm = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (e) => {
        const queryParams = new URLSearchParams();
        if (shippingMethod !== 'all-shipments') {
          queryParams.set('shipping-method', shippingMethod);
        }

        if (shipFromSite !== 'all') {
          queryParams.set('ship-from-site', shipFromSite);
        }

        const queryString = queryParams.toString();

        if (!isDirty) {
          navigate(`/shipping${queryString ? `?${queryString}` : ''}`);
        } else {
          confirm({
            target: e.currentTarget,
            message: (
              <>
                <p>
                  <strong>{t('common.unsavedChanges')}</strong>
                </p>
                <div>{t('common.closeWithoutSaving')}</div>
              </>
            ),
            icon: 'pi pi-exclamation-triangle',
            defaultFocus: 'accept',
            accept: () =>
              navigate(`/shipping${queryString ? `?${queryString}` : ''}`),
          });
        }
      },
      [confirm, isDirty, navigate, shipFromSite, shippingMethod, t],
    );

    const pastDueWorkflowValidation = useCallback(
      () => ({
        isValid: !isPastDueRestricted,
        message: t(
          'shipping.pastDueRestrictionWorkflow',
          pastDueRestrictionDetail,
        ),
      }),
      [isPastDueRestricted, pastDueRestrictionDetail, t],
    );

    const nativeShippingEnabled = useFeatureFlag('shipping_native_workflows');
    const workflowValidation = pastDueRestrictionEnabled
      ? pastDueWorkflowValidation
      : undefined;

    const isUserValidationEnabled = useFeatureFlag('Workflow_User_Validation');
    const isTogActive = useIsTogActive();
    // INTRA storage locations exist only in the TOG database.
    const legacyWorkflows = useMemo(
      () =>
        !isUserValidationEnabled || isTogActive
          ? [WORKFLOW.Ship, WORKFLOW.ShipToIntra]
          : [WORKFLOW.Ship],
      [isUserValidationEnabled, isTogActive],
    );
    const legacyWorkflowMenu = useWorkflowMenu(
      environmentId,
      legacyWorkflows,
      shipmentId,
      undefined,
      workflowValidation,
    );
    const nativeWorkflowMenu = useShippingWorkflowMenu(
      shipmentId,
      undefined,
      workflowValidation,
    );
    const workflowMenu = nativeShippingEnabled
      ? nativeWorkflowMenu
      : legacyWorkflowMenu;

    const [terminalBeefForm] = useShippingFormTerminalBeef();
    const [tripForm] = useShippingTripForm();

    const enableAuditHistoryButton = useFeatureFlag(
      'shipment_audit_history_button',
    );

    const dirtyMenuItemTemplate = useCallback(
      (_item: MenuItem, options: { element: ReactNode }) => (
        <Tooltip>
          <TooltipTrigger
            render={
              <span className="pointer-events-auto block">
                {options.element}
              </span>
            }
          />
          <TooltipContent side="bottom">
            {t('common.saveChangesToEnableActionsAndWorkflows')}
          </TooltipContent>
        </Tooltip>
      ),
      [t],
    );

    const menubar = useMemo<MenuItem[]>(
      () => [
        {
          label: t('common.actions'),
          icon: 'pi pi-sparkles',
          disabled: isDirty,
          template: isDirty ? dirtyMenuItemTemplate : undefined,
          items: [
            {
              label: `Mark as "In Progress"${shipment?.shipmentStatusValue === 'Scheduled' ? ' and will also email the CSR.' : ''}`,
              command: selectInProgress,
            },
          ],
        },
        {
          label: t('common.workflows'),
          icon: 'pi pi-wrench',
          disabled: isDirty,
          template: isDirty ? dirtyMenuItemTemplate : undefined,
          items: workflowMenu,
        },
        {
          label: 'Print Form',
          icon: 'pi pi-print',
          items: [
            {
              label: 'Shipping Form',
              command: printShippingForm,
            },
            {
              label: 'Shipping Form - Terminal Beef',
              command() {
                terminalBeefForm({
                  shipmentId,
                  showPedigree: showPedigreeNames,
                });
              },
            },
            ...(enableShippingFormExport
              ? [
                  {
                    label: t('shipping.exportForm.title'),
                    items: [
                      {
                        label: t('shipping.exportForm.pdf'),
                        command: () => setExportFormat('pdf'),
                      },
                      {
                        label: t('shipping.exportForm.excel'),
                        command: () => setExportFormat('excel'),
                      },
                    ],
                  },
                ]
              : []),
            {
              label: 'Trip Form',
              command() {
                tripForm({
                  shipmentId,
                });
              },
            },
          ],
        },
        ...(enableAuditHistoryButton
          ? [
              {
                label: 'View History',
                icon: PrimeIcons.HISTORY,
                command() {
                  window.open(`/audit/${shipmentId}`, '_blank');
                },
              },
            ]
          : []),
      ],
      [
        dirtyMenuItemTemplate,
        enableAuditHistoryButton,
        enableShippingFormExport,
        isDirty,
        printShippingForm,
        selectInProgress,
        shipment?.shipmentStatusValue,
        shipmentId,
        showPedigreeNames,
        t,
        terminalBeefForm,
        tripForm,
        workflowMenu,
      ],
    );

    return (
      <>
        <ShippingFormExportDialog
          open={exportFormat !== null}
          shipmentIds={[shipmentId]}
          format={exportFormat ?? 'pdf'}
          onClose={() => setExportFormat(null)}
        />
        <Menubar
          model={menubar}
          end={
            <div className="flex flex-row gap-2">
              <Button
                type="submit"
                label={t('common.save')}
                icon="pi pi-save"
                disabled={disableSave}
                loading={loading}
                onClick={onSubmit}
              />
              <Button
                type="button"
                label={t('common.close')}
                icon="pi pi-times"
                severity={isDirty ? 'warning' : 'secondary'}
                onClick={handleCloseForm}
              />
            </div>
          }
        />
      </>
    );
  },
);
